import { TabContext, TabList } from '@mui/lab'
import {
  Box,
  Chip,
  Divider,
  lighten,
  Stack,
  Tab,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import useGetAgentCentralized from '~/features/agents/hooks/useGetAgentCentralized'
import { EAgentKeys } from '~/features/agents/models'
import CampaignAdvanceTimeAction from '~/features/campaigns/components/ofuManageCampaign/campaignAdvanceTimeAction'
import { CampaignEditTabActionsSlot } from '~/features/campaigns/components/ofuManageCampaign/campaignEditTabActionsSlot'
import {
  ECampaignAgentJunctionStatus,
  ECampaignKeys,
  ECampaignExecutionStatus,
  EOfuCampaignTabs,
  TCampaign,
} from '~/features/campaigns/models'
import {
  getCampaignAssistantDisplayName,
  getJunctionExecutionStatus,
  getOfuTabFromRoute,
} from '~/features/campaigns/utils'
import { getCampaignJunctions } from '~/features/campaigns/utils/campaignJunctions'
import useGetSelectedCampaign from '~/hooks/useGetSelectedCampaign'
import { EClientPath } from '~/models/api'
import { ETestId } from '~/models/tests'
import pathBuilder from '~/utils/api'
import rem from '~/utils/ui'

export default function ManageCampaignTabs() {
  const theme = useTheme()
  const { status, campaign, isSharedCampaign } = useGetSelectedCampaign()
  const { selectedAgent, agents } = useGetAgentCentralized()
  const navigate = useNavigate()

  const [selectedTab, setSelectedTab] = useState<EOfuCampaignTabs>(
    EOfuCampaignTabs.contacts,
  )
  const location = useLocation()
  const { id } = useParams()

  const tabFromRoute = getOfuTabFromRoute(location.pathname, id || '')

  useEffect(() => {
    setSelectedTab(tabFromRoute)
  }, [tabFromRoute])

  const campaignJunctions = useMemo(
    () => getCampaignJunctions(campaign as Record<string, unknown>),
    [campaign],
  )
  const assistantsCount = campaignJunctions.length
  const draftAssistantsCount = useMemo(
    () =>
      campaignJunctions.filter(
        (junction) =>
          getJunctionExecutionStatus(junction) ===
          ECampaignAgentJunctionStatus.notStarted,
      ).length,
    [campaignJunctions],
  )
  const shouldShowAssistantsTab = isSharedCampaign
  const selectedAgentId = String(selectedAgent?.[EAgentKeys.id] || '').trim()
  const selectedAgentReferenceName = String(
    selectedAgent?.[EAgentKeys.agentReferenceName] ||
      selectedAgent?.[EAgentKeys.agentName] ||
      selectedAgent?.[EAgentKeys.businessName] ||
      selectedAgentId,
  ).trim()

  const configuredAssistants = useMemo(() => {
    if (campaignJunctions.length > 0) {
      return campaignJunctions
        .map((junction) => {
          const assistantId = String(
            junction.agentId || junction.Agent?.id || '',
          ).trim()
          if (!assistantId) {
            return null
          }
          return {
            id: assistantId,
            label: String(
              getCampaignAssistantDisplayName(junction) || assistantId,
            ).trim(),
          }
        })
        .filter((assistant): assistant is { id: string; label: string } =>
          Boolean(assistant?.id),
        )
    }

    const fallbackAgentId = String(
      (campaign as TCampaign | null)?.[ECampaignKeys.agentId] || '',
    ).trim()

    if (!fallbackAgentId) {
      return []
    }

    const fallbackAgent = agents?.find(
      (agent) => String(agent[EAgentKeys.id] || '').trim() === fallbackAgentId,
    )
    const fallbackAgentLabel = String(
      fallbackAgent?.[EAgentKeys.agentReferenceName] ||
        fallbackAgent?.[EAgentKeys.agentName] ||
        fallbackAgent?.[EAgentKeys.businessName] ||
        fallbackAgentId,
    ).trim()

    return [{ id: fallbackAgentId, label: fallbackAgentLabel }]
  }, [agents, campaign, campaignJunctions])
  const configuredAssistantIds = configuredAssistants.map(
    (assistant) => assistant.id,
  )
  const shouldShowNotConfiguredChip =
    Boolean(selectedAgentId) &&
    configuredAssistantIds.length > 0 &&
    !configuredAssistantIds.includes(selectedAgentId)

  const handleTabChange = useCallback(
    (_: React.SyntheticEvent | null, newTab: EOfuCampaignTabs) => {
      let pathByTab

      switch (newTab) {
        case EOfuCampaignTabs.contacts:
          pathByTab = pathBuilder(EClientPath.campaignOfuContacts, {
            id: id || '',
          })
          break
        case EOfuCampaignTabs.performance:
          pathByTab = pathBuilder(EClientPath.campaignOfuPerformance, {
            id: id || '',
          })
          break
        case EOfuCampaignTabs.assistants:
          pathByTab = pathBuilder(EClientPath.campaignOfuAssistants, {
            id: id || '',
          })
          break
        case EOfuCampaignTabs.edit:
        default:
          pathByTab = pathBuilder(EClientPath.campaignOfuConfiguration, {
            id: id || '',
          })
          break
      }

      setSelectedTab(newTab)
      navigate(pathByTab)
    },
    [id, navigate],
  )

  useEffect(() => {
    if (shouldShowAssistantsTab) {
      return
    }

    if (selectedTab === EOfuCampaignTabs.assistants) {
      handleTabChange(null, EOfuCampaignTabs.contacts)
    }
  }, [handleTabChange, selectedTab, shouldShowAssistantsTab])

  return (
    <Stack
      sx={{
        width: '100%',
        backgroundColor: theme.palette.common.white,
        borderBottom: `${rem(1)} solid ${theme.palette.divider}`,
        boxSizing: 'border-box',
        overflow: 'hidden',
        px: 2,
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <TabContext value={selectedTab}>
          <TabList
            onChange={handleTabChange}
            sx={{
              p: 0,
              maxHeight: rem(44),
              minHeight: rem(44),
              height: rem(44),
              boxSizing: 'border-box',
              flex: 1,
              minWidth: 0,
              '.MuiTabs-flexContainer': {
                gap: 0,
              },
              '.MuiTabs-indicator': {
                backgroundColor: theme.palette.tertiary1,
              },
              '.MuiTab-root': {
                textTransform: 'capitalize',
                fontWeight: 500,
                minWidth: 'auto',
                minHeight: rem(44),
                paddingInline: rem(16),
                '&.Mui-selected': {
                  color: theme.palette.tertiary1,
                  fontWeight: 600,
                },
              },
            }}
          >
            <Tab label="Contacts" value={EOfuCampaignTabs.contacts} />
            {status !== ECampaignExecutionStatus.notStarted && (
              <Tab label="Performance" value={EOfuCampaignTabs.performance} />
            )}
            {shouldShowAssistantsTab && (
              <Tab
                label={
                  <Stack direction="row" alignItems="center" gap={0.75}>
                    <Typography component="span" variant="body2">
                      {`Assistants (${assistantsCount})`}
                    </Typography>
                    {draftAssistantsCount > 0 && (
                      <Chip
                        label={`${draftAssistantsCount} draft`}
                        size="small"
                        sx={{
                          backgroundColor: lighten(
                            theme.palette.warning.light,
                            0.6,
                          ),
                          color: theme.palette.warning.dark,
                          border: `${rem(1)} solid ${theme.palette.warning.main}`,
                          borderRadius: rem(6),
                        }}
                      />
                    )}
                  </Stack>
                }
                value={EOfuCampaignTabs.assistants}
              />
            )}
            <Tab
              label="Edit"
              value={EOfuCampaignTabs.edit}
              data-testid={ETestId.CampaignOfuManageEdit}
            />
          </TabList>
        </TabContext>

        <Stack direction="row" alignItems="center" gap={1}>
          {shouldShowNotConfiguredChip && (
            <Tooltip
              placement="bottom-end"
              leaveDelay={200}
              disableInteractive={false}
              title={
                <Box sx={{ maxWidth: rem(300) }}>
                  <Typography
                    variant="body1"
                    fontSize={rem(14)}
                    sx={{ mb: 0.5 }}
                  >
                    {`This campaign won't run on ${selectedAgentReferenceName}`}
                  </Typography>
                  <Typography variant="body2">
                    Changes you make here - including pausing, editing contacts,
                    or going live - will only affect the assistants this
                    campaign is configured for.
                  </Typography>
                  <Divider
                    sx={{ mt: 1, borderColor: theme.palette.grey[700] }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.grey[400],
                      display: 'block',
                      letterSpacing: rem(0.8),
                      mb: 0.75,
                    }}
                  >
                    CONFIGURED FOR
                  </Typography>
                  <Stack gap={0.5}>
                    {configuredAssistants.map((assistant) => (
                      <Typography
                        key={assistant.id}
                        variant="body2"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.75,
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            width: rem(6),
                            height: rem(6),
                            borderRadius: '50%',
                            backgroundColor: theme.palette.warning.main,
                            display: 'inline-block',
                            flexShrink: 0,
                          }}
                        />
                        {assistant.label}
                      </Typography>
                    ))}
                  </Stack>
                </Box>
              }
            >
              <Chip
                size="small"
                label={
                  <Typography variant="body2" fontSize={rem(12)}>
                    Not configured for:{' '}
                    <Typography
                      component="span"
                      fontWeight={600}
                      fontSize={rem(12)}
                      variant="body2"
                    >
                      {selectedAgentReferenceName}
                    </Typography>
                  </Typography>
                }
                sx={{
                  flexShrink: 0,
                  borderRadius: rem(6),
                  border: `${rem(1)} solid ${theme.palette.warning.light}`,
                  backgroundColor: lighten(theme.palette.warning.light, 0.7),
                  color: theme.palette.warning.dark,
                }}
              />
            </Tooltip>
          )}
          <CampaignAdvanceTimeAction />
          {selectedTab === EOfuCampaignTabs.edit && (
            <CampaignEditTabActionsSlot />
          )}
        </Stack>
      </Stack>
    </Stack>
  )
}
